import _ from 'lodash';
import React from 'react';

import {
  OrderDetailActivityDocument,
  OverrideSkuInput,
  useOverrideLeaseApplicationModalQuery,
  useOverrideLeaseApplicationMutation
} from 'generated/graphql';

import * as types from './types';
import Big from 'big.js';



export interface IData {
  formState: types.IFormState;
}

export function useQuery(orderId: string) {
  const queryTuple = useOverrideLeaseApplicationModalQuery({
    variables: {
      orderId
    }
  });

  return React.useMemo(
    () => {
      const sku2Price = _.keyBy(_.map(queryTuple.data?.order?.lineItems, li => ({
        sku: li.product.sku,
        lineTotalCents: new Big(li.itemTotalCents)
      })), li => li.sku);

      const scannedLineItems = _.map(queryTuple.data?.order?.e1DeviceLedger ?? [], (row): types.IScannedSku => ({
        scannedValue: row.sku,
        newValue: row.sku,
        shouldSend: true
      }));

      const data: IData = {
        formState: {
          applicationNumber: queryTuple.data?.order?.smartPayLeaseApplication?.applicationNumber ?? '',
          // The system only sends items with a line total > 0 to SmartPay, which is why we're filtering on it.
          scannedSkus: _.filter(scannedLineItems, li => _.isNil(sku2Price[li.scannedValue]) || sku2Price[li.scannedValue].lineTotalCents.gt(0))
        }
      };

      return {
        ...queryTuple,
        data
      };
    },
    [queryTuple]
  );
}



export function useOverrideLeaseApplication(orderId: string) {
  const [fnMutate, tuple] = useOverrideLeaseApplicationMutation({
    awaitRefetchQueries: true,
    refetchQueries: [{
      query: OrderDetailActivityDocument,
      variables: { orderId }
    }]
  });

  const fnWrappedMutate = React.useCallback(
    (formState: types.IFormState, leaseApplicationId: string) => fnMutate({
      variables: {
        input: {
          leaseApplicationId,
          applicationNumber: formState.applicationNumber,
          overrideSkus: _.map(formState.scannedSkus, (sku): OverrideSkuInput => ({
            originalSku: sku.scannedValue,
            overrideSku: sku.newValue,
            shouldSend: sku.shouldSend
          }))
        }
      }
    }),
    [fnMutate]
  );

  const wrappedTuple = React.useMemo(
    () => ({
      ...tuple,
      data: null
    }),
    [tuple]
  );

  return [
    fnWrappedMutate,
    wrappedTuple
  ] as const;
}
